/**
 * Start Here Quiz — Progurt
 * Project 09 redesign — 6-step flow (q1 · interstitial · q2 · q3 · email · result)
 *
 * Architecture
 *   - Reads content data from a Liquid-rendered <script type="application/json"
 *     data-quiz-data="concerns-and-bundles"> block emitted by
 *     snippets/start-here-quiz--result.liquid. The JSON carries every concern's
 *     mechanism + Q2 routing tree (from collection metafields), the two doors
 *     (Daily / Daily+) + the hub, and the concern-matched add-on products —
 *     live Shopify prices with preview-safe fallbacks while doors are
 *     unpublished. No client-side hardcoded prices.
 *   - One JS file drives three surfaces (standalone, mid-page, hero-hook) keyed
 *     off the container's data-surface attribute.
 *   - Routing tree (`route()`), reasons (`getReasons()`) and Q1/Q3 universal
 *     options are hardcoded here — they don't vary per locale or per concern,
 *     so they don't need a metafield store.
 *   - Door redo (2026-07-03, quiz-redo-spec.md): route() now returns
 *     { door, addons, depth, concern }. Q3 depth decides the door
 *     (gentle → daily · comprehensive/maintenance → daily-plus · once → reset
 *     [E2, one-time] · returning → hub). The 'practitioner' depth is no longer a Q3 option (removed R10 —
 *     it skipped the email gate); it survives only via the practitioner
 *     deep-link path (?route= share links / ?ref=practitioner). Q2 options may
 *     carry a `door` key (post-migration metafields) or a legacy `bundle`
 *     key — LEGACY_BUNDLE_TO_DOOR maps the latter so today's store data
 *     routes correctly with no store writes. Result page educates before the
 *     price ask (exp_001 law): lead phase → why-this-protocol → protocol card
 *     (concern-matched add-on folded IN as the combined offer) → compare/method links.
 *   - localStorage persistence for guests; Customer API resume hook for
 *     logged-in customers (write at email submission, read at init).
 *   - Klaviyo events emitted via the existing `_klOnsite` queue. Both the new
 *     P09 event names (`quiz_q1_answered`, `quiz_result_shown`, etc.) and the
 *     legacy `Quiz Completed` / `Quiz Abandoned` events fire — flows referencing
 *     the legacy names keep working through the migration window.
 *   - GA4 `quiz_step_entered`, `quiz_step_completed`, `quiz_email_captured`,
 *     `quiz_result_shown`, `quiz_bundle_clicked`, `quiz_abandoned` preserved
 *     with the same param shape; new fields are additive.
 */

(function () {
  'use strict';

  /* ====================================================================
     CONSTANTS
     ==================================================================== */

  var STORAGE_KEY = 'progurt_quiz_v2';
  var EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
  var ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'probe_cohort'];

  // 5 user-facing progress beats: q1 → interstitial → q2 → q3 → email.
  // Result is not part of the progress count.
  var PROGRESS_STEPS = ['q1', 'interstitial', 'q2', 'q3', 'email'];

  // Universal Q1 (locked from comp/quiz-data.jsx)
  var Q1 = {
    question: "What brought you here?",
    sub: "One answer – we'll pick up from there.",
    options: [
      { value: "digestion-food-reactions", label: "Digestion & food reactions",     hint: "Bloating, irregularity, foods that used to feel fine." },
      { value: "immune-skin-inflammation", label: "Immune, skin & inflammation",    hint: "Skin wellbeing, everyday resilience and immune wellness." },
      { value: "brain-mood-behaviour",     label: "Brain, mood & behaviour",        hint: "Fog, mood drift, the stress that won't leave." },
      { value: "performance-recovery",     label: "Performance & recovery",         hint: "Recovery, capacity, output across the week." },
      { value: "mens-health",              label: "Men's health (35+)",             hint: "Vitality, composition, the platform underneath." },
      { value: "womens-health",            label: "Women's health",                 hint: "Cycle and life-stage wellbeing." },
      { value: "not-sure",                 label: "I'm not sure — show me everything", hint: "Browse the six concerns on the hub.", route: "/pages/concerns" }
    ]
  };

  // Universal Q3 (locked from comp/quiz-data.jsx; hints updated for door routing)
  // R10 (2026-07-03, Robert): the fifth "My practitioner pointed me here" option
  // is REMOVED — it let anyone skip the email gate. Practitioner-referred
  // patients arrive via the ?route= share link / ?ref=practitioner instead
  // (see boot() + parseUrlParams()); that deep-link path is unchanged.
  var Q3 = {
    question: "How do you want to start?",
    sub: "We'll match your protocol to where you are.",
    options: [
      { value: "gentle",        label: "Start gentle",              hint: "Ease in. See how the body responds before going deeper." },
      { value: "comprehensive", label: "Go comprehensive",          hint: "The full mineral stack from day one — Mineralise at full depth." },
      { value: "maintenance",   label: "I'm here for the long arc", hint: "Protect what's already built — the monthly rhythm is the platform." },
      // E2: the one-time outcome. Covers no-subscription, no-yogurt-making, and
      // the practitioner-recommended-a-reset arrival in one option.
      { value: "once",          label: "One box first — no subscription", hint: "The complete 16-day reset, billed once. Take the sachets directly — no yogurt-making." },
      { value: "returning",     label: "I've taken Progurt before", hint: "Welcome back. Both protocols are open — you choose." }
    ]
  };

  // MEND phase metadata
  var PHASES = {
    M: { letter: "M", name: "Mineralise", job: "Prepare the terrain",        color: "#02BBD7" },
    E: { letter: "E", name: "Establish",  job: "Reseed the missing strains", color: "#00529C" },
    N: { letter: "N", name: "Nourish",    job: "Feed the ecosystem",         color: "#FFCF2A" },
    D: { letter: "D", name: "Defend",     job: "Maintain the platform",      color: "#ED0678" }
  };

  // Legacy concern-key aliases. Old quiz routed to these values; new quiz uses canonical handles.
  // Keep old Klaviyo flows + bookmarked links working through the migration window.
  var LEGACY_CONCERN_ALIASES = {
    "digestive":     "digestion-food-reactions",
    "gut_lining":    "digestion-food-reactions",
    "immune_skin":   "immune-skin-inflammation",
    "brain_mood":    "brain-mood-behaviour",
    "performance":   "performance-recovery",
    "womens_health": "womens-health",
    "prevention":    "not-sure"
  };

  // Q1 register icon paths (iconographic register)
  var Q1_ICONS = {
    "digestion-food-reactions": '<path d="M12 2v6"/><path d="M9 8h6v3a3 3 0 0 1-6 0V8z"/><path d="M12 11c-3 0-5 2-5 5v3"/><path d="M12 11c3 0 5 2 5 5v3"/>',
    "immune-skin-inflammation": '<path d="M12 2 4 6v6c0 5 4 9 8 10 4-1 8-5 8-10V6l-8-4z"/>',
    "brain-mood-behaviour":     '<path d="M9 3a3 3 0 0 0-3 3v1a3 3 0 0 0-2 5 3 3 0 0 0 0 4 3 3 0 0 0 2 5 3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z"/><path d="M15 3a3 3 0 0 1 3 3v1a3 3 0 0 1 2 5 3 3 0 0 1 0 4 3 3 0 0 1-2 5 3 3 0 0 1-6 0"/>',
    "performance-recovery":     '<path d="M3 18h4l3-12 4 16 3-9h4"/>',
    "mens-health":              '<circle cx="10" cy="14" r="5"/><path d="M14 10l6-6"/><path d="M15 4h5v5"/>',
    "womens-health":            '<circle cx="12" cy="9" r="5"/><path d="M12 14v8"/><path d="M9 19h6"/>',
    "not-sure":                 '<circle cx="12" cy="12" r="9"/><path d="M9 9c0-1.7 1.3-3 3-3s3 1.3 3 3-3 1.8-3 4"/><circle cx="12" cy="17" r="0.5" fill="currentColor"/>'
  };
  var Q3_ICONS = {
    "gentle":        '<path d="M4 12c4 0 4-4 8-4s4 4 8 4"/><path d="M4 18c4 0 4-4 8-4s4 4 8 4"/>',
    "comprehensive": '<rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>',
    "maintenance":   '<path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 4v5h-5"/>',
    "once":          '<path d="M21 8l-9-5-9 5v8l9 5 9-5V8z"/><path d="M3 8l9 5 9-5"/><path d="M12 13v8"/>',
    "returning":     '<circle cx="12" cy="8" r="4"/><path d="M5 21c1.5-4 4-6 7-6s5.5 2 7 6"/><circle cx="18" cy="6" r="2.5" fill="currentColor" stroke="none"/>'
  };

  // Photographic tints (photographic register)
  var PHOTO_TINTS = {
    "digestion-food-reactions": "linear-gradient(135deg, #02BBD7, #0099B0)",
    "immune-skin-inflammation": "linear-gradient(135deg, #ED0678, #B30560)",
    "brain-mood-behaviour":     "linear-gradient(135deg, #FFCF2A, #E5A82E)",
    "performance-recovery":     "linear-gradient(135deg, #00529C, #003C75)",
    "mens-health":              "linear-gradient(135deg, #02BBD7, #00529C)",
    "womens-health":            "linear-gradient(135deg, #ED0678, #C4044F)",
    "not-sure":                 "linear-gradient(135deg, #555, #2c2c2c)"
  };

  /* ====================================================================
     ROUTING TREE — door redo (quiz-redo-spec.md §1)
     ==================================================================== */

  // Legacy bundle handle → door. Today's store-side quiz_q2 metafields carry
  // `bundle` values from the 5-bundle era; this map keeps the quiz routing
  // correctly with NO store writes. The ops migration script
  // (scripts/update-quiz-door-metafields.js) later adds `door` keys, which
  // take precedence when present.
  var LEGACY_BUNDLE_TO_DOOR = {
    'gentle-reset':         'daily',
    'foundation':           'daily',
    'complete-protocol':    'daily-plus',
    'feed-fortify':         'daily-plus',
    'complete-maintenance': 'daily-plus'
  };

  // Concern → suggested add-on handle(s) (quiz-redo-spec.md §2 — DRAFT mapping,
  // Robert sign-off pending). Shown on the result as the subscriber-25% strip.
  var ADDON_MAP = {
    'digestion-food-reactions': ['prebiotic'],
    'immune-skin-inflammation': ['immuno-protein'],
    'brain-mood-behaviour':     ['prebiotic'],
    'performance-recovery':     ['enzymes'],
    'mens-health':              ['enzymes'],
    'womens-health':            ['immuno-protein']
  };

  // Concern-specific add-on framing (overrides the base line carried in the
  // data block). Copy rules: Enzymes = circulation framing, never "digestive
  // enzyme". Immuno = "a specialised globular protein", never lactoferrin.
  var ADDON_CONCERN_LINES = {
    'brain-mood-behaviour': {
      'prebiotic': 'Prebiotic Syrup supports the Nourish phase of the MEND Method.'
    },
    'immune-skin-inflammation': {
      'immuno-protein': 'A specialised globular protein that supports the gut lining — the barrier your immune signals route through.'
    },
    'performance-recovery': {
      'enzymes': 'Enzymes support healthy circulation as part of the Nourish phase.'
    }
  };

  // Mineralise-heavy concerns: gentle starters see the Daily+ lean copy
  // ("your signals lean on Mineralise — Daily+ carries it"). Door stays Daily.
  var MINERALISE_LEAN_CONCERNS = ['performance-recovery', 'womens-health', 'mens-health'];

  function doorFromOption(opt) {
    if (!opt) return null;
    if (opt.door === 'daily' || opt.door === 'daily-plus') return opt.door;
    if (opt.bundle && LEGACY_BUNDLE_TO_DOOR[opt.bundle]) return LEGACY_BUNDLE_TO_DOOR[opt.bundle];
    return null;
  }

  function route(answers, data) {
    var concernKey = answers.q1;
    if (!concernKey || concernKey === 'not-sure') return null;
    var concern = data.concerns[concernKey];
    if (!concern) return null;

    // Q2 signal → door lean. Reads the post-migration `door` key when present;
    // maps today's legacy `bundle` values otherwise. Option-level `addon`
    // (post-migration) takes precedence over the concern-level ADDON_MAP.
    var q2Door = null;
    var q2Addon = null;
    if (concern.q2 && Array.isArray(concern.q2.options)) {
      for (var i = 0; i < concern.q2.options.length; i++) {
        if (concern.q2.options[i].value === answers.q2) {
          q2Door = doorFromOption(concern.q2.options[i]);
          q2Addon = concern.q2.options[i].addon || null;
          break;
        }
      }
    }

    // Q3 depth decides the door (spec §1 table). The 'practitioner' depth is
    // NOT a Q3 option anymore (removed R10 — it skipped the email gate);
    // this branch stays only for the deep-link path (?ref=practitioner sets
    // answers.q3, ?route= share links) and pre-R10 saved state (≤7 days).
    var depth = answers.q3;
    var door;
    if (depth === 'gentle') door = 'daily';
    else if (depth === 'comprehensive' || depth === 'maintenance') door = 'daily-plus';
    else if (depth === 'once') door = 'reset'; // E2: the one-time outcome
    else if (depth === 'returning') door = 'hub';
    else if (depth === 'practitioner') door = q2Door || 'daily-plus';
    else door = q2Door || 'daily';

    var addons = q2Addon ? [q2Addon] : (ADDON_MAP[concernKey] ? ADDON_MAP[concernKey].slice() : []);

    return {
      concern: concernKey,
      door: door,
      bundle: door, // legacy alias — Klaviyo/GA4 param shape + saved-state compat
      addons: addons,
      depth: depth,
      mineraliseLean: depth === 'gentle' && MINERALISE_LEAN_CONCERNS.indexOf(concernKey) > -1,
      returning: depth === 'returning',
      practitioner: depth === 'practitioner'
    };
  }

  /* ====================================================================
     PERSONALISED REASONS — port from comp/quiz-data.jsx getReasons()
     ==================================================================== */

  // Doctrine rules (e1-reconciliation-build-spec.md): the order is the
  // protocol; Nourish = the taught next step; Defend = what the welcome gift
  // equips you for, when you're ready. No legacy bundle names in live copy.
  var SIGNAL_REASON_MAP = {
    "rhythm":               "Supports digestive regularity as you move through the MEND Method.",
    "food":                 "Supports digestive comfort, with Nourish introduced later in the method.",
    "both":                 "Supports digestive regularity and comfort across the longer MEND Method.",
    "skin":                 "Supports the gut environment as part of a broader skin and immune-wellness routine.",
    "cycle":                "Supports a healthy gut environment as part of broader immune wellness.",
    "ambient":              "Supports a healthy gut environment and normal microbial metabolism.",
    "mornings":             "Supports the gut-brain axis as part of a broader clarity and wellbeing routine.",
    "afternoons":           "Supports the gut-brain axis as part of a broader energy and wellbeing routine.",
    "stress":               "Supports the gut-brain axis as part of a broader stress-wellbeing routine.",
    "recovery":             "Supports a healthy gut environment as part of a broader recovery routine.",
    "capacity":             "Supports digestive and gut wellness as part of a broader performance routine.",
    "platform":             "Long-arc maintenance is the Defend phase — the destination your welcome gift equips you for. The monthly rhythm carries it.",
    "vitality":             "Supports nutrient intake and gut wellness as part of a broader vitality routine.",
    "composition":          "Supports digestive function as part of a broader nutrition and body-composition routine.",
    "long-arc":             "The long arc is Defend — the phase your welcome gift equips you for. The monthly rhythm is how the platform holds.",
    "cycle-fertility":      "Supports a healthy gut environment as part of a broader cycle-wellness routine.",
    "pregnancy-postpartum": "Supports a healthy gut environment; consult your healthcare professional during pregnancy or post-partum.",
    "peri-menopause":       "Supports gut wellness and mineral intake as part of a broader perimenopause routine.",
    "post-menopause":       "Supports gut wellness as part of a broader post-menopause routine."
  };

  function getReasons(routing, answers, data) {
    if (!routing) return [];
    var concern = data.concerns[routing.concern];
    if (!concern) return [];
    var doors = data.doors || {};
    var doorKey = routing.door || LEGACY_BUNDLE_TO_DOOR[routing.bundle] || routing.bundle;

    var reasons = [];

    // 1 · Lead-phase education — what the box does inside the method.
    var leadName = (PHASES[concern.leadPhase] || PHASES.E).name;
    var coverage;
    if (doorKey === 'hub') {
      coverage = 'Both protocols carry Mineralise + Establish every month — the order is the protocol.';
    } else if (doorKey === 'reset') {
      coverage = 'Your reset carries Mineralise + Establish intensively — sixteen mapped days, one box.';
    } else if (doorKey === 'daily-plus') {
      coverage = 'Your monthly box carries Mineralise + Establish — with Chloride taking Mineralise to full depth — and your welcome gift equips Defend.';
    } else {
      coverage = 'Your monthly box carries Mineralise + Establish, and your welcome gift equips Defend.';
    }
    reasons.push(
      (concern.short || concern.name) + ' runs the full MEND method, led by ' + leadName + '. ' + coverage
    );

    // 2 · Their Q2 signal, explained.
    if (answers.q2 && SIGNAL_REASON_MAP[answers.q2]) reasons.push(SIGNAL_REASON_MAP[answers.q2]);

    // 3 · Their depth choice, explained — with the Mineralise lean when it applies.
    var plusName = (doors['daily-plus'] && doors['daily-plus'].name) || 'Daily+';
    var dailyName = (doors['daily'] && doors['daily'].name) || 'Daily';
    if (routing.mineraliseLean && doorKey === 'daily') {
      reasons.push('You chose gentle — ' + dailyName + ' is the measured entry. One read from your answers: your signals lean on Mineralise, and ' + plusName + ' carries it at full depth with Chloride, whenever you\'re ready to step up.');
    } else if (routing.depth === 'comprehensive') {
      reasons.push('You chose comprehensive — ' + plusName + ' runs Mineralise at full depth from day one, with Chloride alongside the pH and probiotic sachets.');
    } else if (routing.depth === 'gentle') {
      reasons.push('You chose gentle — ' + dailyName + ' is the entry point, sized for a measured first arc. The method teaches the next step when you\'re ready.');
    } else if (routing.depth === 'once') {
      reasons.push('You chose to start without a subscription — the Reset is the complete 16-day protocol in one box, billed once. If it works for you, the Monthly Protocol holds it from there.');
    } else if (routing.returning) {
      reasons.push('You\'ve taken Progurt before. Both protocols are open — pick up the protocol where you left it, and add any product to your box at 25% off as a subscriber.');
    } else if (routing.practitioner) {
      reasons.push('Your practitioner shared Progurt with you — this is the starting option selected from your answers.');
    } else if (routing.depth === 'maintenance') {
      reasons.push('Long-arc maintenance — the monthly rhythm is the platform. ' + plusName + ' carries the full mineral stack through chapters and seasons.');
    } else {
      reasons.push('Human-origin probiotics at 1 trillion CFU per serving.');
    }

    return reasons.slice(0, 3);
  }

  /* ====================================================================
     STATE
     ==================================================================== */

  var state = {
    step: 'q1',
    answers: { q1: null, q2: null, q3: null, email: null },
    routing: null,
    startedAt: null,
    completedAt: null,
    klaviyoIdentified: false,
    utm: {},
    utmCapturedAt: null
  };

  /* ====================================================================
     PER-INSTANCE BOOT
     ==================================================================== */

  function bootAll() {
    var nodes = document.querySelectorAll('.shq[data-surface]');
    nodes.forEach(function (n) { boot(n); });
  }

  function boot(root) {
    var surface = root.getAttribute('data-surface') || 'standalone';
    var register = root.getAttribute('data-register') || 'typographic';
    var interaction = root.getAttribute('data-interaction') || 'auto-advance';
    var resultsRouting = root.getAttribute('data-results-routing') || 'to-bundle-pdp';
    var magentaKey = root.getAttribute('data-magenta') || 'hpi';
    var b2bMode = root.getAttribute('data-b2b') === 'true';
    var presetConcern = root.getAttribute('data-concern') || '';

    // Klaviyo client-side subscribe — AU defaults; override per-market via data-attrs.
    // On email submit the lead is subscribed to the "Quiz Leads" list (single opt-in),
    // which triggers SS | Quiz Recommendation (then Welcome Series).
    // Runbook: progurt-ops/docs/plans/quiz-klaviyo-recommendation-flow.md
    var klaviyoCompanyId = root.getAttribute('data-klaviyo-company') || 'LXg6aQ';
    var quizLeadsListId = root.getAttribute('data-quiz-leads-list') || 'YaaRJw';

    var data = readData(root); root.setAttribute('data-clarity-mask', 'true'); // 2026-07-17: mask quiz UI in Clarity replays (health answers)

    var instance = {
      root: root,
      surface: surface,
      register: register,
      interaction: interaction,
      resultsRouting: resultsRouting,
      magentaKey: magentaKey,
      b2bMode: b2bMode,
      klaviyoCompanyId: klaviyoCompanyId,
      quizLeadsListId: quizLeadsListId,
      data: data,
      practitionerRef: false,
      firstRender: true
    };

    restoreStoredAttribution();
    parseUrlParams(instance);
    if (instance.practitionerRef) instance.b2bMode = true;

    // Render Q1/Q3 options into the mount points
    renderQ1Options(instance);
    renderQ3Options(instance);

    // Hero-hook: only renders Q1; no resume + no email gate
    if (surface === 'hero-hook') {
      bindHeroHookHandlers(instance);
      return;
    }

    bindHandlers(instance);

    // Restore from localStorage (guest) — standalone surface only.
    // Mid-page embeds always start fresh.
    if (surface === 'standalone') {
      restoreLocalState(instance);
    }

    // Pre-fill from ?concern= deep link (from hero-hook)
    if (presetConcern && data.concerns[presetConcern]) {
      state.answers.q1 = presetConcern;
      if (state.step === 'q1') state.step = 'interstitial';
    }

    // Result-shortcut: ?route=concern-door (practitioner-shared link).
    // Accepts door handles (daily, daily-plus, hub) AND legacy bundle handles
    // (pre-redo shared links keep resolving — mapped through LEGACY_BUNDLE_TO_DOOR).
    var routeParam = getQueryParam('route');
    if (routeParam) {
      var doorsData = data.doors || {};
      var parts = routeParam.split('-');
      for (var i = parts.length - 1; i > 0; i--) {
        var c = parts.slice(0, i).join('-');
        var b = parts.slice(i).join('-');
        // E2: legacy gentle-reset share links = practitioners who recommended
        // exactly this product — route them to its relaunch (the Reset), not
        // to daily. Share-link path ONLY; the Q2 metafield mapping is untouched.
        if (b === 'gentle-reset') b = 'reset';
        var doorKey = doorsData[b] ? b : LEGACY_BUNDLE_TO_DOOR[b] || null;
        if (data.concerns[c] && doorKey) {
          state.answers.q1 = c;
          state.answers.q2 = (data.concerns[c].q2.options[0] || {}).value || null;
          state.answers.q3 = 'practitioner';
          state.routing = {
            concern: c,
            door: doorKey,
            bundle: doorKey,
            addons: ADDON_MAP[c] ? ADDON_MAP[c].slice() : [],
            depth: 'practitioner',
            practitioner: true
          };
          state.step = 'result';
          break;
        }
      }
    }

    state.startedAt = state.startedAt || new Date().toISOString();

    renderStep(instance);
    fireKlaviyo('quiz_started', { surface: surface, register: register, interaction_pattern: interaction });
  }

  function readData(root) {
    var node = root.querySelector('script[data-quiz-data="concerns-and-bundles"]');
    if (!node) return { concerns: {}, doors: {}, addons: {}, magenta: { phrase: 'Human Probiotic Isolates™' } };
    try { return JSON.parse(node.textContent); }
    catch (e) { return { concerns: {}, doors: {}, addons: {}, magenta: { phrase: 'Human Probiotic Isolates™' } }; }
  }

  /* ====================================================================
     URL PARSING
     ==================================================================== */

  function parseUrlParams(instance) {
    var params = new URLSearchParams(window.location.search);

    var concern = params.get('concern');
    if (concern) {
      if (LEGACY_CONCERN_ALIASES[concern]) concern = LEGACY_CONCERN_ALIASES[concern];
      if (concern === 'not-sure') {
        window.location.href = localizedPath('/pages/concerns');
        return;
      }
      if (instance.data.concerns[concern]) {
        state.answers.q1 = concern;
        state.step = 'interstitial';
      }
    }

    if (params.get('returning') === 'true') state.answers.q3 = 'returning';
    if (params.get('ref') === 'practitioner') {
      instance.practitionerRef = true;
      state.answers.q3 = 'practitioner';
    }

    var hasCurrentAttribution = ATTRIBUTION_KEYS.some(function (k) { return !!params.get(k); });
    if (hasCurrentAttribution) {
      state.utm = {};
      ATTRIBUTION_KEYS.forEach(function (k) {
        var v = params.get(k);
        if (v) state.utm[k] = v;
      });
      state.utmCapturedAt = Date.now();
    }
  }

  function getQueryParam(name) {
    return new URLSearchParams(window.location.search).get(name);
  }

  // Prefix a root-absolute path with the active Shopify Markets root (window.Shopify.routes.root,
  // which always ends in '/'). On the primary market root is '/' → no-op (single-market AU unaffected);
  // on a subfolder market (e.g. '/en-de/') it keeps cart/discount/page navigations in-market instead of
  // bouncing the visitor back to the root domain (wrong currency/language). Idempotent: leaves absolute
  // URLs and already-prefixed paths untouched, so values already localized by Liquid never double-prefix.
  function localizedPath(p) {
    if (typeof p !== 'string' || !p) return p;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (/^https?:\/\//.test(p)) return p;
    if (root !== '/' && p.lastIndexOf(root, 0) === 0) return p;
    return root + p.replace(/^\//, '');
  }

  /* ====================================================================
     OPTION RENDERING
     ==================================================================== */

  function renderQ1Options(instance) {
    var mount = instance.root.querySelector('[data-quiz-options="q1"]');
    if (!mount) return;
    var opts = instance.surface === 'hero-hook'
      ? Q1.options.filter(function (o) { return o.value !== 'not-sure'; })
      : Q1.options;
    mount.innerHTML = opts.map(function (o, i) { return optionHtml(o, 'q1', i, instance); }).join('');
  }

  function renderQ3Options(instance) {
    var mount = instance.root.querySelector('[data-quiz-options="q3"]');
    if (!mount) return;
    mount.innerHTML = Q3.options.map(function (o, i) { return optionHtml(o, 'q3', i, instance); }).join('');
  }

  function renderQ2Options(instance) {
    var mount = instance.root.querySelector('[data-quiz-options="q2"]');
    if (!mount) return;
    var concernKey = state.answers.q1;
    var concern = instance.data.concerns[concernKey];
    if (!concern || !concern.q2 || !Array.isArray(concern.q2.options) || !concern.q2.options.length) {
      mount.innerHTML = '<p class="shq__error">Q2 metafield missing for ' + escapeHtml(concernKey || '') + '. See start-here-quiz handoff §1.</p>';
      return;
    }
    mount.innerHTML = concern.q2.options.map(function (o, i) { return optionHtml(o, 'q2', i, instance); }).join('');
    var qEl = instance.root.querySelector('[data-quiz-q2-question]');
    if (qEl) qEl.textContent = concern.q2.question || '';
    var ebEl = instance.root.querySelector('[data-quiz-q2-eyebrow]');
    if (ebEl) ebEl.textContent = 'Step 2 of 3 · ' + (concern.short || concern.name);
  }

  function optionHtml(opt, stepKey, idx, instance) {
    var iconHtml = '';
    if (instance.register === 'iconographic') {
      var p = '';
      if (stepKey === 'q1') p = Q1_ICONS[opt.value] || '';
      if (stepKey === 'q3') p = Q3_ICONS[opt.value] || '';
      if (stepKey === 'q2') p = '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>';
      iconHtml = p
        ? '<span class="shq__option-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' + p + '</svg></span>'
        : '';
    }
    var photo = '';
    if (instance.register === 'photographic') {
      var tint = (stepKey === 'q1' && PHOTO_TINTS[opt.value]) ? PHOTO_TINTS[opt.value] : 'linear-gradient(135deg,#555,#222)';
      var glyph = (opt.label || '').charAt(0).toLowerCase();
      photo = '<span class="shq__option-photo" aria-hidden="true" style="background:' + tint + ';"><span>' + escapeHtml(glyph) + '</span></span>';
    }
    var num = '';
    if (instance.register === 'editorial') {
      num = '<span class="shq__option-num" aria-hidden="true">' + ('0' + (idx + 1)).slice(-2) + '</span>';
    }
    var hint = (instance.surface !== 'hero-hook' && opt.hint)
      ? '<span class="shq__option-hint">' + escapeHtml(opt.hint) + '</span>'
      : '';
    var routeAttr = opt.route ? ' data-quiz-route="' + escapeAttr(opt.route) + '"' : '';

    return '<button type="button" class="shq__option" role="radio" aria-checked="false"' +
      ' data-value="' + escapeAttr(opt.value) + '"' +
      ' data-quiz-answer="' + escapeAttr(opt.value) + '"' +
      ' data-quiz-step-key="' + stepKey + '"' + routeAttr + '>' +
      iconHtml + photo + num +
      '<span class="shq__option-marker" aria-hidden="true"></span>' +
      '<span class="shq__option-body">' +
        '<span class="shq__option-label">' + escapeHtml(opt.label || '') + '</span>' +
        hint +
      '</span>' +
      '<span class="shq__option-arrow" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>' +
      '</span>' +
    '</button>';
  }

  /* ====================================================================
     HANDLERS
     ==================================================================== */

  function bindHandlers(instance) {
    var root = instance.root;

    root.addEventListener('click', function (e) {
      var optBtn = e.target.closest('.shq__option');
      if (optBtn && root.contains(optBtn)) {
        return handleOption(instance, optBtn);
      }
      if (e.target.closest('[data-quiz-back]')) return handleBack(instance);
      if (e.target.closest('[data-quiz-continue]')) return advance(instance);
      if (e.target.closest('[data-quiz-copy-link]')) return handleCopyLink(instance);
      var cta = e.target.closest('[data-quiz-cta]');
      if (cta) {
        var ctaType = cta.getAttribute('data-quiz-cta');
        fireKlaviyo('quiz_bundle_clicked', {
          concern: state.routing && state.routing.concern,
          bundle: state.routing && state.routing.bundle,
          cta_position: ctaType
        });
        fireGA4('quiz_bundle_clicked', {
          quiz_bundle: state.routing && state.routing.bundle,
          quiz_concern: state.routing && state.routing.concern,
          quiz_cta_position: ctaType
        });
      }
      var closeBtn = e.target.closest('.shq__close');
      if (closeBtn) {
        fireGA4('quiz_abandoned', { quiz_last_step: state.step, quiz_has_email: !!state.answers.email });
        exitQuiz();
      }
    });

    // Escape closes the full-screen standalone quiz (matches the X button).
    // Scoped to standalone so the inline mid-page embeds don't hijack Esc.
    root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && instance.surface === 'standalone') {
        // Don't hijack Escape inside a text field — users press it to dismiss
        // the browser's autocomplete, not to abandon the quiz.
        var tag = e.target && e.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.preventDefault();
        fireGA4('quiz_abandoned', { quiz_last_step: state.step, quiz_has_email: !!state.answers.email });
        exitQuiz();
        return;
      }
      // Keyboard nav within radiogroups (up/down arrow keys)
      var optBtn = e.target.closest('.shq__option');
      if (!optBtn) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        var next = optBtn.nextElementSibling;
        if (next && next.classList.contains('shq__option')) next.focus();
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        var prev = optBtn.previousElementSibling;
        if (prev && prev.classList.contains('shq__option')) prev.focus();
      } else if (e.key === ' ' || e.key === 'Enter') {
        // Default button click semantics handle this
      }
    });

    var form = root.querySelector('[data-quiz-email-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        handleEmailSubmit(instance);
      });
    }

    // Note: no popstate handler. We use history.replaceState per step (not
    // pushState) so the quiz occupies a single history entry. Browser back
    // exits the quiz to the previous page — matching the X button. Stepping
    // backward between quiz steps is handled by the ← Back button (handleBack)
    // which mutates state directly. Pre-P09 history.pushState per step caused
    // a loop with this listener: popstate fired → renderStep → pushed another
    // entry → next back click no-op'd.

    window.addEventListener('beforeunload', function () {
      if (state.step !== 'result' && state.step !== 'q1') {
        fireGA4('quiz_abandoned', { quiz_last_step: state.step, quiz_has_email: !!state.answers.email });
        if (state.answers.email) {
          fireKlaviyo('Quiz Abandoned', { last_step: state.step, email: state.answers.email, answers: state.answers });
        }
      }
    });
  }

  function bindHeroHookHandlers(instance) {
    instance.root.addEventListener('click', function (e) {
      var optBtn = e.target.closest('.shq__option');
      if (!optBtn) return;
      var value = optBtn.getAttribute('data-value');
      var routeUrl = optBtn.getAttribute('data-quiz-route');
      fireKlaviyo('quiz_hero_hook_picked', { surface: 'hero-hook', value: value });
      fireGA4('quiz_q1_answered', { quiz_concern: value, quiz_surface: 'hero-hook' });
      if (routeUrl) {
        window.location.href = localizedPath(routeUrl);
      } else {
        window.location.href = localizedPath('/pages/start-here?concern=' + encodeURIComponent(value));
      }
    });
  }

  /* ====================================================================
     STEP TRANSITIONS
     ==================================================================== */

  function handleOption(instance, btn) {
    var stepKey = btn.getAttribute('data-quiz-step-key');
    var value = btn.getAttribute('data-value');
    var routeUrl = btn.getAttribute('data-quiz-route');

    if (stepKey === 'q1' && value === 'not-sure' && routeUrl) {
      fireGA4('quiz_escape_to_concerns_hub', { quiz_surface: instance.surface,
        utm_source: state.utm.utm_source || '',
        utm_medium: state.utm.utm_medium || '',
        utm_campaign: state.utm.utm_campaign || '',
        utm_content: state.utm.utm_content || '',
        utm_term: state.utm.utm_term || '',
        probe_cohort: state.utm.probe_cohort || '' });
      window.location.href = routeUrl;
      return;
    }

    state.answers[stepKey] = value;
    saveLocalState();

    var group = btn.parentNode;
    if (group) {
      var siblings = group.querySelectorAll('.shq__option');
      for (var i = 0; i < siblings.length; i++) {
        siblings[i].classList.remove('shq__option--selected');
        siblings[i].setAttribute('aria-checked', 'false');
      }
    }
    btn.classList.add('shq__option--selected');
    btn.setAttribute('aria-checked', 'true');

    if (stepKey === 'q1') fireKlaviyo('quiz_q1_answered', { concern: value, surface: instance.surface });
    if (stepKey === 'q2') fireKlaviyo('quiz_q2_answered', { concern: state.answers.q1, q2_value: value });
    if (stepKey === 'q3') fireKlaviyo('quiz_q3_answered', { concern: state.answers.q1, q3_value: value });

    var ga = { quiz_step_name: stepKey };
    ga['quiz_' + stepKey + '_value'] = value;
    fireGA4('quiz_step_completed', ga);

    if (instance.interaction === 'auto-advance' ||
        instance.interaction === 'swipe' ||
        instance.interaction === 'one-per-screen') {
      setTimeout(function () { advance(instance, stepKey); }, prefersReducedMotion() ? 0 : 220);
    }
  }

  function advance(instance, fromKey) {
    fromKey = fromKey || state.step;
    if (fromKey === 'q1') state.step = 'interstitial';
    else if (fromKey === 'interstitial') state.step = 'q2';
    else if (fromKey === 'q2') state.step = 'q3';
    else if (fromKey === 'q3') {
      state.routing = route(state.answers, instance.data);
      if (state.routing && (state.routing.returning || state.routing.practitioner || instance.b2bMode)) {
        if (instance.surface !== 'standalone') return redirectToStandaloneResult(instance);
        state.step = 'result';
      } else {
        state.step = 'email';
      }
    } else if (fromKey === 'email') {
      if (instance.surface !== 'standalone') return redirectToStandaloneResult(instance);
      state.step = 'result';
    }
    renderStep(instance);
  }

  // The D33 canon result page is a full-surface treatment (D29 hero +
  // 3-up reasons + timeline + mechanism + trust strip). Forced into the
  // mid-page embed column, every grid collapses and the result reads as
  // a 2000px-tall ribbon. On any non-standalone surface, complete the
  // quiz then redirect to /pages/start-here for the full reveal. The
  // standalone surface's restoreLocalState picks up the result-state
  // we save below and lands the customer directly on the result step.
  function redirectToStandaloneResult(instance) {
    state.routing = state.routing || route(state.answers, instance.data);
    if (!state.routing) return;
    state.step = 'result';
    saveLocalState();
    fireKlaviyo('quiz_embed_completed', {
      from_surface: instance.surface,
      concern: state.routing.concern,
      bundle: state.routing.bundle,
      door: state.routing.door || state.routing.bundle,
      had_email: !!state.answers.email
    });
    fireGA4('quiz_embed_completed', {
      quiz_from_surface: instance.surface,
      quiz_concern: state.routing.concern,
      quiz_bundle: state.routing.bundle,
      quiz_door: state.routing.door || state.routing.bundle
    });
    window.location.href = localizedPath('/pages/start-here?from=' + encodeURIComponent(instance.surface));
  }

  function handleBack(instance) {
    if (state.step === 'result') state.step = 'email';
    else if (state.step === 'email') state.step = 'q3';
    else if (state.step === 'q3') state.step = 'q2';
    else if (state.step === 'q2') state.step = 'interstitial';
    else if (state.step === 'interstitial') state.step = 'q1';
    renderStep(instance);
  }

  function handleEmailSubmit(instance) {
    var input = instance.root.querySelector('[data-quiz-email-input]');
    var errEl = instance.root.querySelector('[data-quiz-email-error]');
    var email = (input.value || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      input.setAttribute('aria-invalid', 'true');
      if (errEl) errEl.hidden = false;
      return;
    }
    input.setAttribute('aria-invalid', 'false');
    if (errEl) errEl.hidden = true;
    state.answers.email = email;
    state.routing = state.routing || route(state.answers, instance.data);
    captureEmail(instance, email);
    fireKlaviyo('quiz_email_captured', {
      concern: state.routing && state.routing.concern,
      bundle: state.routing && state.routing.bundle,
      email: email,
      utm: state.utm
    });
    fireGA4('quiz_email_captured', {
      quiz_concern: state.routing && state.routing.concern,
      quiz_bundle: state.routing && state.routing.bundle
    });
    advance(instance, 'email');
  }

  // X / Close — exit the quiz entirely rather than stepping backward. The
  // ← Back button handles per-step backward nav inside the quiz; X is meant
  // to abandon the quiz. Prefer document.referrer when it's a same-origin
  // non-quiz page (the customer came from somewhere — return them there),
  // else go home.
  function exitQuiz() {
    var referrer = document.referrer || '';
    var origin = window.location.origin;
    var sameOrigin = referrer.indexOf(origin) === 0;
    var notQuiz = referrer.indexOf('/pages/start-here') === -1;
    if (sameOrigin && notQuiz) {
      window.location.href = referrer;
    } else {
      window.location.href = localizedPath('/');
    }
  }

  function handleCopyLink(instance) {
    if (!state.routing) return;
    var shareDoor = state.routing.door || state.routing.bundle;
    var url = window.location.origin + localizedPath('/pages/start-here?route=' +
      encodeURIComponent(state.routing.concern + '-' + shareDoor) + '&ref=practitioner');
    try { navigator.clipboard.writeText(url); } catch (e) {}
    var btn = instance.root.querySelector('[data-quiz-copy-link]');
    if (btn) {
      var orig = btn.textContent;
      btn.textContent = 'Link copied ✓';
      setTimeout(function () { btn.textContent = orig; }, 2000);
    }
    fireKlaviyo('quiz_practitioner_link_copied', {
      concern: state.routing.concern,
      bundle: state.routing.bundle,
      door: state.routing.door || state.routing.bundle
    });
  }

  /* ====================================================================
     RENDERING
     ==================================================================== */

  function renderStep(instance) {
    var root = instance.root;
    root.setAttribute('data-step', state.step);
    var container = root.querySelector('.shq__container');
    if (container) {
      container.setAttribute('data-step', state.step);
      container.setAttribute('data-quiz-step', state.step);
    }

    var concernKey = state.answers.q1;
    var concern = concernKey && instance.data.concerns[concernKey];
    if (concern) {
      root.style.setProperty('--shq-accent', 'var(--color-' + (concern.accent || 'black') + ')');
      root.style.setProperty('--shq-accent-hex', concern.accentHex || '#0D0D0D');
    } else {
      root.style.removeProperty('--shq-accent');
      root.style.removeProperty('--shq-accent-hex');
    }

    var steps = root.querySelectorAll('.shq__step');
    for (var i = 0; i < steps.length; i++) {
      steps[i].hidden = steps[i].getAttribute('data-quiz-step') !== state.step;
    }

    if (state.step === 'q2') renderQ2Options(instance);
    if (state.step === 'interstitial') renderInterstitial(instance);
    if (state.step === 'result') renderResult(instance);

    updateProgress(instance);

    if (state.step === 'email') {
      var subEl = root.querySelector('[data-quiz-email-sub]');
      var emailRow = root.querySelector('[data-quiz-email-row]');
      var b2b = instance.b2bMode || (state.routing && state.routing.practitioner);
      if (subEl) {
        subEl.textContent = b2b
          ? "Practitioner mode — we'll skip the email step. Click through for your recommendation."
          : "Drop your email to see your protocol and what to expect.";
      }
      if (emailRow && b2b) {
        emailRow.innerHTML =
          '<span class="shq__email-thanks">Practitioner mode active</span>' +
          '<button type="submit" class="shq__email-submit" data-quiz-email-submit>See the recommendation</button>';
      }
    }

    // replaceState (not pushState) — the quiz holds a single history entry.
    // The hash is for analytics + linkability; the back button exits the
    // quiz instead of stepping backward inside it.
    if (instance.surface === 'standalone' && window.history && history.replaceState) {
      try { history.replaceState({ shqStep: state.step }, '', '#step-' + state.step); } catch (e) {}
    }

    fireGA4('quiz_step_entered', { quiz_step_name: state.step });

    if (instance.surface === 'standalone' && !instance.firstRender) {
      try { root.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
    }

    // Move focus to the new step's heading. Without this, the previously
    // focused control is hidden by [hidden] and focus falls back to <body>:
    // keyboard users are dumped to the top of the page and screen-reader users
    // get no announcement that the question/result changed. Skip the initial
    // page-load render (firstRender) so we don't steal focus on load.
    if (!instance.firstRender) {
      var activeStep = root.querySelector('.shq__step:not([hidden])');
      var focusTarget = activeStep && activeStep.querySelector('h1, h2, [data-quiz-result-h]');
      if (focusTarget) {
        if (!focusTarget.hasAttribute('tabindex')) focusTarget.setAttribute('tabindex', '-1');
        try { focusTarget.focus({ preventScroll: true }); } catch (e) { focusTarget.focus(); }
      }
    }

    // Embedded quiz: ratchet the stage to its tallest step so moving to a shorter
    // question never shrinks the quiz and reflows the page upward. The page stays
    // anchored where the reader left it — no auto-scroll. (Review 2026-06-04.)
    if (instance.surface !== 'standalone') {
      var stageEl = root.querySelector('.shq__stage');
      var liveStep = root.querySelector('.shq__step:not([hidden])');
      if (stageEl && liveStep && !liveStep.classList.contains('shq__step--result')) {
        var curMin = parseInt(stageEl.style.minHeight, 10) || 0;
        var stepH = liveStep.offsetHeight;
        if (stepH > curMin) { stageEl.style.minHeight = stepH + 'px'; }
      }
    }

    instance.firstRender = false;
  }

  function renderInterstitial(instance) {
    var concern = instance.data.concerns[state.answers.q1];
    if (!concern) return;
    var root = instance.root;
    setText(root, '[data-quiz-interstitial-tag]', 'Your ' + (concern.short || concern.name) + ' · the gut routing');

    var hEl = root.querySelector('[data-quiz-interstitial-h]');
    if (hEl) hEl.innerHTML = emphasiseLast((concern.mechanism && concern.mechanism.h) || concern.name);

    setText(root, '[data-quiz-interstitial-lede]', (concern.mechanism && concern.mechanism.lede) || '');

    var phasesEl = root.querySelector('[data-quiz-phases]');
    if (phasesEl) {
      var order = ['M', 'E', 'N', 'D'];
      phasesEl.innerHTML = order.map(function (k) {
        var on = (concern.phases || []).indexOf(k) > -1;
        return '<div class="shq__phases-cell' + (on ? ' shq__phases-cell--on' : '') + '" style="--phase-color:' + PHASES[k].color + ';">' +
          '<span class="shq__phases-letter">' + k + '</span>' +
          '<span class="shq__phases-name">' + PHASES[k].name + '</span>' +
          '<span class="shq__phases-job">' + PHASES[k].job + '</span>' +
        '</div>';
      }).join('');
    }
  }

  function renderResult(instance) {
    var routing = state.routing || route(state.answers, instance.data);
    state.routing = routing;
    if (!routing) return;
    var concern = instance.data.concerns[routing.concern];
    var doors = instance.data.doors || {};
    // Normalise: a stale localStorage routing (pre-redo, ≤7 days old) carries a
    // legacy bundle handle — map it to its door so the result renders correctly.
    var doorKey = routing.door || LEGACY_BUNDLE_TO_DOOR[routing.bundle] || routing.bundle;
    if (doorKey !== 'hub' && !doors[doorKey]) doorKey = 'daily';
    var door = doors[doorKey];
    if (!concern || !door) return;
    routing.door = doorKey;
    routing.bundle = doorKey; // legacy event-param alias
    if (!routing.addons) routing.addons = ADDON_MAP[routing.concern] ? ADDON_MAP[routing.concern].slice() : [];
    var isHub = doorKey === 'hub';
    var isReset = doorKey === 'reset'; // E2: one-time framing — no /mo, no add-on fold
    var priceDisplay = door.price ? (isHub ? 'from ' + door.price + '/mo' : isReset ? door.price + ' · one-time' : door.price + '/mo') : '';

    var root = instance.root;
    root.style.setProperty('--shq-accent', 'var(--color-' + (concern.accent || 'black') + ')');
    root.style.setProperty('--shq-accent-hex', concern.accentHex || '#0D0D0D');

    var ebEl = root.querySelector('[data-quiz-result-eyebrow]');
    if (ebEl) {
      var ebText = 'Your protocol · ' + (concern.short || concern.name);
      if (routing.returning) ebText += ' · welcome back';
      if (routing.practitioner) ebText += ' · practitioner referral';
      ebEl.textContent = ebText;
    }

    // ── 1 · EDUCATION FIRST — the lead phase, before any price ──
    var hEl = root.querySelector('[data-quiz-result-h]');
    if (hEl) {
      var leadName = (PHASES[concern.leadPhase] || PHASES.E).name;
      hEl.innerHTML = 'Your protocol starts with <em>' + escapeHtml(leadName) + '</em>.';
    }
    setText(root, '[data-quiz-result-lede]', (concern.mechanism && concern.mechanism.lede) || '');

    var phasesEl = root.querySelector('[data-quiz-result-phases]');
    if (phasesEl) {
      phasesEl.innerHTML = ['M', 'E', 'N', 'D'].map(function (p, i) {
        var ph = PHASES[p] || {};
        var name = escapeHtml(ph.name || p);
        var sep = i > 0 ? ' → ' : '';
        if (p === concern.leadPhase) {
          return sep + '<span style="color:' + (ph.color || '#0D0D0D') + ';font-weight:600;">' + name + '</span>';
        }
        return sep + name;
      }).join('');
    }
    var leadEl = root.querySelector('[data-quiz-result-lead-phase]');
    if (leadEl) {
      var leadColor = (PHASES[concern.leadPhase] || PHASES.E).color;
      var leadPhaseName = (PHASES[concern.leadPhase] || PHASES.E).name;
      leadEl.innerHTML = '<span style="color:' + leadColor + ';font-weight:600;">' + escapeHtml(leadPhaseName) + '</span>';
    }

    // Phase-coverage grid (hero education column) — same cells as the interstitial.
    var gridEl = root.querySelector('[data-quiz-result-phasegrid]');
    if (gridEl) {
      gridEl.innerHTML = ['M', 'E', 'N', 'D'].map(function (k) {
        var on = (concern.phases || []).indexOf(k) > -1;
        return '<div class="shq__phases-cell' + (on ? ' shq__phases-cell--on' : '') + '" style="--phase-color:' + PHASES[k].color + ';">' +
          '<span class="shq__phases-letter">' + k + '</span>' +
          '<span class="shq__phases-name">' + PHASES[k].name + '</span>' +
          '<span class="shq__phases-job">' + PHASES[k].job + '</span>' +
        '</div>';
      }).join('');
    }

    // ── 2 · Why this protocol — the personalised reasons ──
    var reasonsEl = root.querySelector('[data-quiz-reasons]');
    if (reasonsEl) {
      reasonsEl.innerHTML = getReasons(routing, state.answers, instance.data).map(function (r, i) {
        return '<div class="shq__reason">' +
          '<span class="shq__reason-num">' + ('0' + (i + 1)).slice(-2) + '</span>' +
          '<span class="shq__reason-body">' + escapeHtml(r) + '</span>' +
        '</div>';
      }).join('');
    }

    // ── 3 · The protocol card — add-on promoted INTO the combined offer ──
    // Resolve the concern-matched add-on(s) up front so the card headline, the
    // title-area tag, and the in-card add-on block all speak the combined
    // "Daily+ + Enzymes — your monthly protocol" offer (Robert, R4d). No add-on
    // fold on the hub (returning) card — that stays the "both protocols" view.
    var addonData = instance.data.addons || {};
    // No add-on fold on the hub (returning) card OR the one-time Reset card —
    // the combined-offer language ("your monthly protocol", the 25%-subscriber
    // line) is subscription copy. The Reset PDP bridges to Monthly itself.
    var addonCards = (isHub || isReset) ? [] : (routing.addons || []).map(function (h) { return addonData[h]; }).filter(Boolean);
    var featuredAddon = addonCards[0] || null;
    var addonNames = addonCards.map(function (a) { return a.name; }).join(' + ');

    var doorH = root.querySelector('[data-quiz-door-h]');
    if (doorH) {
      if (isHub) {
        doorH.textContent = 'Welcome back — both protocols are open.';
      } else if (isReset) {
        doorH.textContent = (door.name || '') + ' — the complete protocol, one box, once.';
      } else if (featuredAddon) {
        doorH.textContent = (door.name || '') + ' + ' + addonNames + ' — your monthly protocol';
      } else {
        doorH.textContent = (door.name || '') + ' — the protocol your answers point to.';
      }
    }

    // Title-area tag names the add-on inside the card head.
    var addonTagEl = root.querySelector('[data-quiz-bundle-addon-tag]');
    if (addonTagEl) {
      if (featuredAddon) {
        addonTagEl.textContent = 'with ' + addonNames;
        addonTagEl.hidden = false;
      } else {
        addonTagEl.hidden = true;
      }
    }

    setText(root, '[data-quiz-bundle-glyph]', door.glyph || (door.name || '').slice(0, 2));
    setText(root, '[data-quiz-bundle-name]', door.name);
    setText(root, '[data-quiz-bundle-name-inline]', door.name);
    setText(root, '[data-quiz-bundle-tagline]', door.tagline || '');
    setText(root, '[data-quiz-bundle-desc]', door.description || '');
    setText(root, '[data-quiz-bundle-price]', priceDisplay);
    setText(root, '[data-quiz-bundle-duration]', door.duration || '');

    var compareEl = root.querySelector('[data-quiz-bundle-compare]');
    if (compareEl) {
      if (door.compareAt) { compareEl.textContent = door.compareAt; compareEl.hidden = false; }
      else { compareEl.hidden = true; }
    }

    var insideEl = root.querySelector('[data-quiz-bundle-inside]');
    var insideWrap = root.querySelector('[data-quiz-bundle-inside-wrap]');
    if (insideEl && Array.isArray(door.whatsInside) && door.whatsInside.length) {
      insideEl.innerHTML = door.whatsInside.map(function (it) { return '<li>' + escapeHtml(it) + '</li>'; }).join('');
      if (insideWrap) insideWrap.hidden = false;
    } else if (insideWrap) {
      insideWrap.hidden = true;
    }

    // Mineralise lean note (gentle depth on a Mineralise-heavy concern):
    // Daily stays the recommendation; Daily+ is named as the deeper carry.
    var leanEl = root.querySelector('[data-quiz-door-lean]');
    if (leanEl) {
      var plusDoor = doors['daily-plus'];
      if (routing.mineraliseLean && doorKey === 'daily' && plusDoor) {
        leanEl.innerHTML = 'Your signals lean on Mineralise — <strong>' + escapeHtml(plusDoor.name || 'Daily+') +
          '</strong> carries it at full depth with Chloride. ' +
          '<a href="' + escapeAttr(plusDoor.url || '#') + '" data-quiz-cta="lean-upgrade">See ' + escapeHtml(plusDoor.name || 'Daily+') + ' →</a>';
        leanEl.hidden = false;
      } else {
        leanEl.hidden = true;
      }
    }

    var primary, secondary;
    if (isHub) {
      primary = { href: door.url, label: 'Compare both protocols', target: 'hub' };
      secondary = { href: concern.url, label: 'Or read about ' + (concern.short || concern.name), target: 'concern' };
    } else {
      primary = { href: door.url, label: isReset ? 'Start your reset' : 'Start ' + door.name, target: 'bundle' };
      secondary = { href: concern.url, label: 'Or read about ' + (concern.short || concern.name), target: 'concern' };
      if (instance.resultsRouting === 'via-concern-page') {
        primary = { href: concern.url, label: 'Read the full ' + (concern.short || concern.name) + ' page', target: 'concern' };
        secondary = { href: door.url, label: 'Skip ahead — start ' + door.name, target: 'bundle' };
      } else if (instance.resultsRouting === 'hybrid') {
        secondary = { href: concern.url, label: 'Read the full ' + (concern.short || concern.name) + ' page', target: 'concern' };
      }
    }
    var primaryEl = root.querySelector('[data-quiz-cta-primary]');
    var secondaryEl = root.querySelector('[data-quiz-cta-secondary]');
    if (primaryEl) {
      primaryEl.href = primary.href;
      primaryEl.setAttribute('data-quiz-cta', primary.target);
      primaryEl.setAttribute('data-quiz-recommendation', door.handle);
      primaryEl.setAttribute('data-quiz-route', routing.concern + '/' + doorKey);
      var labelEl = primaryEl.querySelector('[data-quiz-bundle-cta-label]');
      if (labelEl) labelEl.textContent = primary.label;
    }
    if (secondaryEl) {
      secondaryEl.href = secondary.href;
      secondaryEl.setAttribute('data-quiz-cta', secondary.target);
      secondaryEl.textContent = secondary.label;
    }

    var showCopy = !!(instance.b2bMode || routing.practitioner);
    var copyBtn = root.querySelector('[data-quiz-copy-link]');
    if (copyBtn) copyBtn.hidden = !showCopy;
    var copyHint = root.querySelector('[data-quiz-copy-hint]');
    if (copyHint) copyHint.hidden = !showCopy;

    var confirmEl = root.querySelector('[data-quiz-email-confirm]');
    if (confirmEl) {
      if (state.answers.email) {
        confirmEl.innerHTML = 'A copy of this recommendation is on its way to <strong>' + escapeHtml(state.answers.email) + '</strong>.';
        confirmEl.hidden = false;
      } else {
        confirmEl.hidden = true;
      }
    }

    // The door's arc (timeline). Hidden when the door carries no timeline (hub).
    var timelineEl = root.querySelector('[data-quiz-timeline]');
    var timelineWrap = root.querySelector('[data-quiz-timeline-wrap]');
    var timeline = door.timeline || [];
    if (timelineEl) {
      timelineEl.innerHTML = timeline.map(function (t) {
        return '<div class="shq__timeline-cell">' +
          '<span class="shq__timeline-when">' + escapeHtml(t.when || '') + '</span>' +
          '<span class="shq__timeline-line">' + escapeHtml(t.line || '') + '</span>' +
        '</div>';
      }).join('');
    }
    if (timelineWrap) timelineWrap.hidden = !timeline.length;

    // Add-on folded INTO the card — the concern-matched product promoted as
    // part of the combined monthly protocol (R4d), not a separate afterthought
    // strip. Concern-matched reason + the approved 25%-subscriber line live
    // inside the recommendation card. Primary CTA still routes to the protocol
    // PDP; each add-on carries its own discovery link (data-quiz-cta="addon").
    var cardAddonWrap = root.querySelector('[data-quiz-card-addon]');
    var cardAddonList = root.querySelector('[data-quiz-card-addon-list]');
    if (cardAddonList) {
      if (addonCards.length) {
        cardAddonList.innerHTML = addonCards.map(function (a) {
          var line = (ADDON_CONCERN_LINES[routing.concern] && ADDON_CONCERN_LINES[routing.concern][a.handle]) || a.line || '';
          var ph = PHASES[a.phase] || PHASES.N;
          return '<div class="shq__card-addon-item">' +
            '<span class="shq__card-addon-phase" style="color:' + ph.color + ';">' + escapeHtml(ph.name) + '</span>' +
            '<a class="shq__card-addon-name" href="' + escapeAttr(a.url || '#') + '" data-quiz-cta="addon">' + escapeHtml(a.name || '') + ' →</a>' +
            '<span class="shq__card-addon-line">' + escapeHtml(line) + '</span>' +
          '</div>';
        }).join('');
        if (cardAddonWrap) cardAddonWrap.hidden = false;
      } else if (cardAddonWrap) {
        cardAddonWrap.hidden = true;
      }
    }

    // ── 4 · Mechanism + method links (concern page · full method · hub) ──
    setText(root, '[data-quiz-mechanism-h]', (concern.mechanism && concern.mechanism.h) || '');
    setText(root, '[data-quiz-mechanism-lede]', (concern.mechanism && concern.mechanism.lede) || '');
    var mechLink = root.querySelector('[data-quiz-mechanism-link]');
    if (mechLink) mechLink.href = concern.url;

    var stickyEl = root.querySelector('[data-quiz-sticky-cta]');
    if (stickyEl && instance.surface === 'standalone') {
      stickyEl.hidden = false;
      setText(root, '[data-quiz-sticky-bundle]', door.name);
      setText(root, '[data-quiz-sticky-price]', priceDisplay);
      setText(root, '[data-quiz-sticky-duration]', door.duration || '');
      var stickyBtn = root.querySelector('[data-quiz-sticky-cta-btn]');
      if (stickyBtn) {
        stickyBtn.href = primary.href;
        stickyBtn.textContent = primary.label;
        stickyBtn.setAttribute('data-quiz-recommendation', door.handle);
      }
    }

    state.completedAt = new Date().toISOString();
    fireKlaviyo('quiz_result_shown', {
      concern: routing.concern,
      bundle: doorKey, // legacy param name, door value — flows keep firing
      door: doorKey,
      addons: (routing.addons || []).join(','),
      duration_seconds: state.startedAt ? Math.round((Date.now() - new Date(state.startedAt).getTime()) / 1000) : 0,
      register: instance.register,
      interaction_pattern: instance.interaction,
      results_routing: instance.resultsRouting
    });
    fireKlaviyo('Quiz Completed', {
      recommendation: doorKey,
      health_concerns: [routing.concern],
      probiotic_experience: routing.depth,
      approach: routing.depth
    });
    fireGA4('quiz_result_shown', {
      quiz_concern: routing.concern,
      quiz_bundle: doorKey,
      quiz_door: doorKey,
      quiz_addons: (routing.addons || []).join(','),
      quiz_register: instance.register,
      quiz_interaction_pattern: instance.interaction,
      quiz_duration_seconds: state.startedAt ? Math.round((Date.now() - new Date(state.startedAt).getTime()) / 1000) : 0
    });

    clearLocalState();
  }

  function updateProgress(instance) {
    var stepIdx = PROGRESS_STEPS.indexOf(state.step);
    var total = PROGRESS_STEPS.length;
    var pct = stepIdx < 0 ? 100 : Math.min(100, (stepIdx / total) * 100);
    var fills = instance.root.querySelectorAll('.shq__progress-fill, .shq__mid-progress-fill');
    for (var i = 0; i < fills.length; i++) fills[i].style.width = pct + '%';

    var counterValue;
    if (state.step === 'q1' || state.step === 'interstitial') counterValue = 1;
    else if (state.step === 'q2') counterValue = 2;
    else if (state.step === 'q3' || state.step === 'email') counterValue = 3;
    else counterValue = 3;

    var counters = instance.root.querySelectorAll('.shq__step-current');
    for (var j = 0; j < counters.length; j++) counters[j].textContent = counterValue;
  }

  /* ====================================================================
     PERSISTENCE
     ==================================================================== */

  function saveLocalState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        step: state.step,
        answers: state.answers,
        routing: state.routing,
        startedAt: state.startedAt,
        utm: state.utm,
        utmCapturedAt: state.utmCapturedAt
      }));
    } catch (e) {}
  }

  function restoreStoredAttribution() {
    if (!window.ProgurtQuizAttribution || typeof window.ProgurtQuizAttribution.read !== 'function') return;
    var stored = window.ProgurtQuizAttribution.read();
    if (!stored) return;
    state.utm = stored.values || {};
    state.utmCapturedAt = stored.capturedAt || null;
  }

  function restoreLocalState(/* instance */) {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var parsed = JSON.parse(raw);
      if (!parsed.startedAt) return;
      if ((Date.now() - new Date(parsed.startedAt).getTime()) > EXPIRY_MS) {
        clearLocalState();
        return;
      }
      if (getQueryParam('concern') || getQueryParam('retake')) return;
      state.answers = parsed.answers || state.answers;
      state.step = parsed.step || 'q1';
      state.routing = parsed.routing || null;
      state.startedAt = parsed.startedAt;
    } catch (e) {
      clearLocalState();
    }
  }

  function clearLocalState() {
    try {
      var stored = window.ProgurtQuizAttribution && typeof window.ProgurtQuizAttribution.read === 'function'
        ? window.ProgurtQuizAttribution.read()
        : null;
      if (stored) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          utm: stored.values,
          utmCapturedAt: stored.capturedAt
        }));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {}
  }

  /* ====================================================================
     EMAIL CAPTURE — Klaviyo identify + Shopify /contact fallback
     ==================================================================== */

  function captureEmail(instance, email) {
    var payload = {
      $email: email,
      quiz_health_concern: state.routing && state.routing.concern,
      quiz_recommendation: state.routing && state.routing.bundle,
      quiz_door: state.routing && (state.routing.door || state.routing.bundle),
      quiz_q1_answer: state.answers.q1,
      quiz_q2_answer: state.answers.q2,
      quiz_q3_answer: state.answers.q3,
      quiz_depth: state.routing && state.routing.depth,
      quiz_completed_at: new Date().toISOString(),
      quiz_surface: instance.surface,
      quiz_register: instance.register
    };
    var hadKlaviyoQueue = !!window._klOnsite;
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['identify', payload]);
    state.klaviyoIdentified = true;

    // Queue identify before sending the consented Quiz Leads subscription. The
    // subscription remains fire-and-forget and never blocks the quiz.
    subscribeToQuizLeads(instance, email);
    if (hadKlaviyoQueue) return;

    // Fallback: Shopify /contact form (best-effort)
    try {
      var form = new FormData();
      form.append('contact[email]', email);
      form.append('contact[tags]', 'quiz-' + (state.routing && state.routing.concern || ''));
      form.append('form_type', 'customer');
      fetch(localizedPath('/contact#contact_form'), { method: 'POST', body: form }).catch(function () {});
    } catch (e) {}
  }

  /* ====================================================================
     KLAVIYO + GA4
     ==================================================================== */

  function fireKlaviyo(eventName, data) {
    try {
      if (!state.klaviyoIdentified) return; // Consent/identity gate 2026-07-17: Klaviyo health events require the labelled email action and a queued identify. The consented subscribe payload carries the email; GA4 keeps de-identified funnel events.
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['track', eventName, data || {}]);
    } catch (e) {}
  }

  function fireGA4(eventName, params) {
    try {
      if (typeof window.gtag !== 'function') return;
      params = params || {};
      params.event_category = 'quiz';
      window.gtag('event', eventName, params);
    } catch (e) {}
  }

  // Subscribe the quiz lead to the "Quiz Leads" list via Klaviyo's public
  // client-side endpoint (public company id — no secret in the browser). The
  // list is single opt-in, so this subscribes immediately and triggers the
  // MP | Quiz Recommendation flow.
  // Hardened 2026-07-07: third-party scripts that wrap window.fetch (Okendo
  // loyalty is a known offender) can reject/throw on this call, and the old
  // fire-and-forget swallowed every failure — leads vanished with no signal.
  // Now: fetch attempt → XMLHttpRequest fallback (immune to fetch wrappers)
  // → GA4 telemetry either way so capture failures are visible in analytics.
  function sendKlaviyoSubscribe(url, body, onDone) {
    function xhrFallback(why) {
      try {
        var x = new XMLHttpRequest();
        x.open('POST', url, true);
        x.setRequestHeader('revision', '2024-10-15');
        x.setRequestHeader('content-type', 'application/json');
        x.onload = function () {
          onDone(x.status >= 200 && x.status < 300 ? null : why + '+xhr_status_' + x.status);
        };
        x.onerror = function () { onDone(why + '+xhr_network'); };
        x.send(body);
      } catch (e) { onDone(why + '+xhr_throw'); }
    }
    try {
      fetch(url, {
        method: 'POST',
        headers: { 'revision': '2024-10-15', 'content-type': 'application/json' },
        body: body,
        keepalive: true
      }).then(function (res) {
        onDone(res.ok ? null : 'fetch_status_' + res.status);
      }).catch(function () { xhrFallback('fetch_reject'); });
    } catch (e) { xhrFallback('fetch_throw'); }
  }

  function subscribeToQuizLeads(instance, email) {
    if (!instance.klaviyoCompanyId || !instance.quizLeadsListId) return;
    try {
      var properties = {
        quiz_health_concern: state.routing && state.routing.concern,
        quiz_recommendation: state.routing && state.routing.bundle,
        quiz_depth: state.routing && state.routing.depth,
        quiz_surface: instance.surface
      };
      // Attribution stamp: progurt-ops config/quiz-capture-ingest.yaml pins these
      // exact property names (quiz_utm_* / quiz_probe_cohort) and its capture
      // sidecar fails closed until it observes them on a live profile — rename
      // only in lockstep with that config. Only non-empty captured values ride.
      ATTRIBUTION_KEYS.forEach(function (k) {
        if (state.utm[k]) properties['quiz_' + k] = state.utm[k];
      });
      var payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Start Here Quiz',
            profile: { data: { type: 'profile', attributes: {
              email: email,
              properties: properties
            } } }
          },
          relationships: { list: { data: { type: 'list', id: instance.quizLeadsListId } } }
        }
      };
      var url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(instance.klaviyoCompanyId);
      sendKlaviyoSubscribe(url, JSON.stringify(payload), function (err) {
        if (err) {
          fireGA4('quiz_subscribe_error', { error_detail: err });
          if (window.console && console.error) console.error('[quiz] Klaviyo subscribe failed:', err);
        } else {
          fireGA4('quiz_subscribe_sent');
        }
      });
    } catch (e) {}
  }

  /* ====================================================================
     HELPERS
     ==================================================================== */

  function setText(root, selector, value) {
    var el = root.querySelector(selector);
    if (el) el.textContent = value || '';
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function escapeAttr(s) {
    return escapeHtml(s).replace(/"/g, '&quot;');
  }

  function emphasiseLast(text) {
    if (!text) return '';
    var m = String(text).match(/^(.*\s)(\S+)\s*$/);
    if (m) return escapeHtml(m[1]) + '<em>' + escapeHtml(m[2]) + '</em>';
    return escapeHtml(text);
  }

  function prefersReducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    catch (e) { return false; }
  }

  /* ====================================================================
     BOOT
     ==================================================================== */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  // Expose for debug + extension integration
  window.ShqRoute = route;
  window.ShqLegacyAliases = LEGACY_CONCERN_ALIASES;
  window.ShqLegacyBundleToDoor = LEGACY_BUNDLE_TO_DOOR;
  window.ShqAddonMap = ADDON_MAP;
})();

/* cdn-propagation touch 2026-07-20 - content unchanged */
